import React, {useMemo} from 'react';
import {Button, Icon, Text} from '../../components';
import {thinMargins} from '../../constants';
import {ArticleCard} from './ArticleCard';
import {colors} from '../../theme';
import {LearnArticle} from '../../types';
import {usePageWidth, useScreenSize, useCarouselNavigation} from '../../utils';

interface IArticleCarousel {
  articles: LearnArticle[];
  title: string;
  subtitle?: string;
  context?: 'articlePage' | 'mainSite' | 'parentArticles' | 'parentPage';
}

export const ArticleCarousel = ({
  articles,
  title,
  subtitle,
  context = 'mainSite',
}: IArticleCarousel) => {
  const screenSize = useScreenSize();
  const pageWidth = usePageWidth();

  const isArticlePage =
    context === 'articlePage' || context === 'parentArticles';

  const breakpointConfig = useMemo((): {
    remPerItem: number;
    marginLeft: number;
    lastSlideIndex: number;
  } => {
    if (screenSize === 'xx-large') {
      return {
        remPerItem: 23.5,
        marginLeft: (pageWidth - 1280) / 2,
        lastSlideIndex:
          articles.length > 1 ? articles.length - 2 : articles.length,
      };
    } else if (screenSize === 'extra-large') {
      return {
        remPerItem: 23.5,
        marginLeft: 128,
        lastSlideIndex:
          articles.length > 1 ? articles.length - 2 : articles.length,
      };
    } else if (screenSize === 'large') {
      return {
        remPerItem: 23.5,
        marginLeft: 128,
        lastSlideIndex:
          articles.length > 1 ? articles.length - 1 : articles.length,
      };
    } else if (screenSize === 'medium') {
      return {
        remPerItem: 19.5,
        marginLeft: isArticlePage ? 64 : 32,
        lastSlideIndex:
          articles.length > 1 ? articles.length - 1 : articles.length,
      };
    } else {
      return {
        remPerItem: 19.5,
        marginLeft: 32,
        lastSlideIndex: articles.length,
      };
    }
  }, [screenSize, pageWidth, isArticlePage, articles.length]);

  const {
    currentSlideIndex,
    onGoToPreviousSlide,
    onGoToNextSlide,
    swipeHandlers,
  } = useCarouselNavigation({
    itemCount: breakpointConfig.lastSlideIndex,
    wrap: true,
  });

  const transformSize = `translatex(-${
    currentSlideIndex * breakpointConfig.remPerItem
  }rem)`;

  return (
    <div className="relative">
      {isArticlePage ? (
        <div className={`flex  justify-between pb-6 ${thinMargins}`}>
          <Text as="h3" variant="Subtitle1">
            {title}
          </Text>
          <div className="min-w-fit">
            {context === 'articlePage' && (
              <Button
                articleLink={{
                  categoryTitle: title,
                }}
                label={'View all'}
              />
            )}
            {context === 'parentArticles' && (
              <Button
                articleLink={{
                  isParents: true,
                  categoryTitle: title,
                }}
                label={'View all'}
              />
            )}
          </div>
        </div>
      ) : (
        <div
          className={`max-w-screen-xl flex flex-col lg:flex-row justify-center md:items-center lg:items-start md:text-center lg:text-start ${thinMargins} lg:mx-32 pb-8 lg:pb-10`}>
          <div className="lg:w-2/3">
            <Text as="h3" variant="Title" className="text-black pb-2 lg:pb-6">
              {title}
            </Text>
            {subtitle && (
              <Text as="p" variant="LargeBody" className="text-black">
                {subtitle}
              </Text>
            )}
          </div>
          {context === 'parentPage' ? (
            <div className={`hidden lg:block lg:ml-auto lg:mr-0 mt-auto mb-0`}>
              <Button label={'View more articles'} linkTo="parents/guides" />
            </div>
          ) : (
            <div className={`hidden lg:block lg:ml-auto lg:mr-0 mt-auto mb-0`}>
              <Button label={'View more articles'} linkTo="articles" />
            </div>
          )}
        </div>
      )}

      <div
        {...swipeHandlers}
        style={{
          marginLeft: `${breakpointConfig.marginLeft}px`,
        }}
        className={`${isArticlePage && 'max-w-[80rem] lg:mr-32'}`}>
        <div
          className={` ${
            isArticlePage
              ? ' overflow-hidden lg:overflow-visible'
              : ' overflow-hidden '
          }`}>
          <div
            className={`flex transition ease-out duration-500 gap-4  h-[24.5rem] lg:h-[27.5rem]`}
            style={{
              transform: transformSize,
            }}>
            {articles.map((a, i) => (
              <div
                key={i}
                className={` ${
                  isArticlePage
                    ? 'min-w-[18.5rem] w-[18.5rem] lg:min-w-[26rem] lg:max-w-[26rem]'
                    : '  '
                }`}>
                <ArticleCard
                  key={i}
                  title={a.title}
                  subtitle={a.subtitle}
                  slug={a.slug}
                  categoryColor={a.categoryColor}
                  categoryTitle={a.categoryTitle}
                  image={a.image}
                  isFullWidth={isArticlePage ? true : false}
                  isParentsArticle={
                    context === 'parentArticles' || context === 'parentPage'
                  }
                  categories={a.categories}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      {!isArticlePage && (
        <div className="flex lg:hidden justify-center pt-6">
          <Button
            label={'View more articles'}
            linkTo={context === 'parentPage' ? 'parents/guides' : 'articles'}
          />
        </div>
      )}
      <div
        className={` ${
          isArticlePage ? 'lg:hidden' : 'lg:flex'
        } absolute top-[55%] w-full hidden lg:justify-between `}
        style={{
          paddingLeft: `${breakpointConfig.marginLeft - 100}px`,
          paddingRight: `${breakpointConfig.marginLeft - 100}px`,
        }}>
        <button onClick={onGoToPreviousSlide}>
          <Icon iconName={'left'} fill={colors.white} height={75} width={75} />
        </button>
        <button onClick={onGoToNextSlide}>
          <Icon iconName={'right'} fill={colors.white} height={75} width={75} />
        </button>
      </div>
    </div>
  );
};
